<template>
  <aside
    class="verified-anomaly-detail"
    :class="{ 'verified-anomaly-detail--embedded': embedded }"
    aria-labelledby="verifiedAnomalyDetailTitle"
    @keydown.esc.stop="close">
    <header class="verified-detail-header">
      <div>
        <h2 id="verifiedAnomalyDetailTitle">{{ finding.friendlyId || finding.id }}</h2>
      </div>
      <v-btn icon small :disabled="busy" aria-label="Close anomaly details" @click="close"
        ><v-icon>mdi-close</v-icon></v-btn
      >
    </header>
    <div class="verified-detail-body">
      <section class="detail-section">
        <h3>{{ linkTitle }}</h3>
        <!-- AVA findings: the inspection tag is a field of the form, saved with the other edits. -->
        <InspectionPickerAutocomplete
          v-if="linkMode === 'inspection'"
          :value="draftInspection"
          :disabled="busy || !!linkDisabledReason"
          :show-scope-toggle="false"
          class="inspection-field"
          @input="setDraftInspection" />
        <div v-else class="linked-component-actions">
          <button class="linked-component" type="button" :disabled="busy || !!linkDisabledReason" @click="link">
            <v-icon small>{{ linkedEntity ? 'mdi-link-variant' : 'mdi-link-variant-plus' }}</v-icon>
            <span>{{ linkedEntity ? linkedEntity.name || linkedEntity.friendlyId || 'Linked inspection unavailable' : 'Not linked' }}</span>
            <v-icon small aria-hidden="true">mdi-menu-down</v-icon>
          </button>
          <v-btn v-if="linkedEntity && linkMode !== 'inspection'" icon small color="error" :disabled="busy || !!linkDisabledReason" :aria-label="`Remove ${linkEntityName.toLowerCase()} link`" @click="unlink">
            <v-icon small>mdi-link-variant-off</v-icon>
          </v-btn>
        </div>
        <p v-if="linkedEntity && linkMode !== 'inspection'" class="muted">{{ linkEntityName }}</p>
        <p v-if="linkDisabledReason" class="muted">{{ linkDisabledReason }}</p>
      </section>
      <section class="detail-section">
        <h3>Engineering details</h3>
        <p v-if="dirty" class="draft-notice" role="status">
          {{
            refreshedWhileEditing
              ? 'Anomaly refreshed. Your unsaved edits are preserved; review them before saving.'
              : 'Unsaved changes'
          }}
        </p>
        <v-combobox
          v-model="draft.category"
          :items="categories"
          label="Category"
          dense
          outlined
          hide-details="auto"
          :disabled="busy" />
        <v-combobox
          v-model="draft.type"
          :items="types"
          label="Type"
          dense
          outlined
          hide-details="auto"
          :disabled="busy" />
        <v-combobox
          v-model="draft.component"
          :items="componentOptions"
          :return-object="false"
          item-text="name"
          item-value="name"
          label="Detected component"
          :hint="detectedComponentHint"
          persistent-hint
          dense
          outlined
          :disabled="busy" />
        <v-textarea
          v-model="draft.locationDescription"
          label="Location"
          placeholder="Where the finding is on the component"
          rows="1"
          auto-grow
          dense
          outlined
          hide-details="auto"
          :disabled="busy" />
        <v-select
          v-model="draft.coatingBreakdown"
          :items="optionsFor(coatingOptions, draft.coatingBreakdown)"
          item-text="name"
          item-value="name"
          label="Coating breakdown"
          dense
          outlined
          hide-details="auto"
          :disabled="busy"
          clearable />
        <v-select
          v-model="draft.pittingCategory"
          :items="optionsFor(pittingOptions, draft.pittingCategory)"
          item-text="name"
          item-value="name"
          label="Corrosion / pitting category"
          dense
          outlined
          hide-details="auto"
          :disabled="busy"
          clearable>
          <template #selection="{ item }">
            <v-chip small label class="category-chip" v-bind="pittingChip(item)">{{ item.name }}</v-chip>
          </template>
          <template #item="{ item }">
            <v-chip small label class="category-chip" v-bind="pittingChip(item)">{{ item.name }}</v-chip>
            <span class="category-hint">{{ pittingHint(item) }}</span>
          </template>
        </v-select>
        <v-select
          v-model="draft.defectCategorization"
          :items="optionsFor(defectOptions, draft.defectCategorization)"
          item-text="name"
          item-value="name"
          label="Defect categorization"
          dense
          outlined
          hide-details="auto"
          :disabled="busy"
          clearable>
          <template #selection="{ item }">
            <v-chip small label class="category-chip" v-bind="defectChip(item)">{{ item.name }}</v-chip>
          </template>
          <template #item="{ item }">
            <v-chip small label class="category-chip" v-bind="defectChip(item)">{{ item.name }}</v-chip>
          </template>
        </v-select>
        <v-select
          v-model="draft.severity"
          :items="priorityOptions"
          label="Work order priority"
          dense
          outlined
          hide-details="auto"
          :disabled="busy"
          clearable />
        <v-textarea
          v-model="draft.description"
          label="Engineering summary"
          rows="3"
          auto-grow
          dense
          outlined
          hide-details="auto"
          :disabled="busy" />
      </section>
      <section class="detail-section">
        <h3>Source evidence</h3>
        <img
          v-if="finding.tiledImageUriPath && !imageFailed"
          :src="finding.tiledImageUriPath"
          class="evidence-image"
          :alt="`Evidence for anomaly ${finding.friendlyId || finding.id}`"
          @error="imageFailed = true" />
        <p v-else class="muted">Anomaly image unavailable</p>
        <dl>
          <dt>Scan</dt> <dd>{{ scanName || 'Scan unavailable' }}</dd>
          <template v-if="linkMode !== 'inspection'">
            <dt>AI model</dt> <dd>{{ finding.aiModel || 'Not recorded' }}</dd>
          </template>
          <dt>Detection confidence</dt> <dd>{{ confidence }}</dd>
        </dl>
        <template v-if="showActions">
        <v-btn
          v-if="finding.analysisJobId"
          small
          outlined
          :disabled="busy"
          @click="$emit('source-report', finding)"
          >Open source analysis report</v-btn
        >
        <p v-else class="muted">Source analysis report not recorded</p>
        </template>
      </section>
      <section v-if="showActions" class="detail-section">
        <h3>Engineering actions</h3>
        <div class="detail-actions">
          <v-btn small outlined :disabled="busy" @click="$emit('report', finding)"
            >Finding report</v-btn
          ><v-btn small outlined :disabled="busy || !canIsolate" @click="$emit('isolate', finding)"
            >Show in 3D</v-btn
          ><v-btn small outlined :disabled="busy || !hasPoint" @click="$emit('corrosion', finding)"
            >Corrosion Analyser</v-btn
          >
        </div>
        <p v-if="!canIsolate" class="muted">
          3D context is unavailable: no point or model geometry.
        </p>
        <p v-if="!hasPoint" class="muted">Corrosion Analyser requires a valid 3D point.</p>
      </section>
    </div>
    <p class="ai-content-notice" role="note">
      <v-icon small aria-hidden="true">mdi-creation</v-icon>
      <span>AI-generated content. It may be inaccurate: review and verify it before relying on it.</span>
    </p>
    <footer class="verified-detail-footer">
      <v-btn small text :disabled="busy || !dirty" @click="resetDraft">Cancel edits</v-btn
      ><v-btn
        small
        depressed
        color="primary"
        :loading="busy"
        :disabled="busy || !dirty"
        @click="save"
        >Save changes</v-btn
      >
    </footer>
  </aside>
</template>

<script>
  import {
    component,
    coatingBreakdown,
    pittingCategory,
    defectCategorization,
  } from '../../config/enums';
  import { componentTypeOf } from '../../utils/findingComponents';
  import { formatPercent } from '../../utils/reportFormat';
  import { hasSpatialPoint } from '../../utils/verifiedAnomalyViewer';
  import { categoryStyle } from '@/features/Tags/snaps/avaFindings';
  import InspectionPickerAutocomplete from '@/features/Tags/components/monitoring/InspectionPickerAutocomplete.vue';

  const idOf = (entity) => (entity && entity.id) || null;

  const EDITABLE_FIELDS = [
    'category',
    'type',
    'component',
    'locationDescription',
    'coatingBreakdown',
    'pittingCategory',
    'defectCategorization',
    'severity',
    'description',
  ];
  const NEUTRAL_CHIP = Object.freeze({ color: '#8a8f98', 'text-color': '#ffffff' });
  /** Black or white text, whichever reads on `hex` ("#rrggbb" or "#rrggbbaa"). */
  const inkOn = (hex) => {
    const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i.exec(hex || '');
    if (!m) return '#ffffff';
    const [r, g, b] = m.slice(1).map((h) => parseInt(h, 16) / 255);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.6 ? '#1f1f1f' : '#ffffff';
  };
  const sameField = (field, left, right) => {
    const normalize = (value) =>
      value == null ? '' : field === 'severity' ? String(value) : value;
    return normalize(left) === normalize(right);
  };
  export default {
    name: 'VerifiedAnomalyDetailPanel',
    components: { InspectionPickerAutocomplete },
    props: {
      finding: { type: Object, required: true },
      scanName: { type: String, default: '' },
      busy: { type: Boolean, default: false },
      /** Source report and engineering actions; off where the host has no report, 3D or analyser to open. */
      showActions: { type: Boolean, default: true },
      /** Fills its container (a dialog) instead of docking as a 380 px side panel. */
      embedded: { type: Boolean, default: false },
      /** When set, the current linked entity cannot be changed and this says why. */
      linkDisabledReason: { type: String, default: '' },
      /** AVA Findings uses the inspection tag relationship; AI Inspect retains component linking. */
      linkMode: { type: String, default: 'component' },
    },
    data() {
      return {
        draft: {},
        draftBaseline: {},
        /** Inspection tag picked in the form (inspection link mode); linked on save. */
        draftInspection: null,
        draftBaselineInspectionId: null,
        draftId: null,
        refreshedWhileEditing: false,
        imageFailed: false,
        categories: ['Corrosion', 'Damage'],
        types: [
          'Surface Rust',
          'Uniform',
          'Pitting',
          'Crevice',
          'Galvanic',
          'Intergranular',
          'Filiform',
          'Cladding',
        ],
        componentOptions: component,
        coatingOptions: coatingBreakdown,
        pittingOptions: pittingCategory,
        defectOptions: defectCategorization,
      };
    },
    computed: {
      fieldsDirty() {
        return EDITABLE_FIELDS.some(
          (field) => !sameField(field, this.draft[field], this.finding[field]),
        );
      },
      inspectionDirty() {
        return this.linkMode === 'inspection' && idOf(this.draftInspection) !== idOf(this.finding.inspection);
      },
      dirty() {
        return this.fieldsDirty || this.inspectionDirty;
      },
      priorityOptions() {
        const values = ['1', '2', '3', '4'];
        if (this.draft.severity != null && !values.includes(String(this.draft.severity)))
          values.push(String(this.draft.severity));
        return values.map((value) => ({ text: `Level ${value}`, value }));
      },
      canIsolate() {
        return !!(
          this.hasPoint ||
          (this.finding.masterTagId && componentTypeOf(this.finding.masterTagType)) ||
          (this.finding.dbIds || []).length ||
          ((this.finding.inspection || {}).children || []).some(
            (child) => child && child.dbId != null,
          )
        );
      },
      hasPoint() {
        return hasSpatialPoint(this.finding.point3d);
      },
      confidence() {
        return formatPercent(this.finding.detectionConfidence);
      },
      linkedEntity() {
        if (this.linkMode === 'inspection') return this.finding.inspection || null;
        return this.finding.masterTagId
          ? { id: this.finding.masterTagId, name: this.finding.masterTagName, friendlyId: this.finding.masterTagId }
          : null;
      },
      linkEntityName() {
        return this.linkMode === 'inspection' ? 'Inspection tag' : 'Component';
      },
      linkTitle() {
        return `Linked ${this.linkEntityName.toLowerCase()}`;
      },
      detectedComponentHint() {
        return this.linkMode === 'inspection'
          ? 'Detected description; separate from the linked inspection tag above.'
          : 'Detected description; separate from the linked component above.';
      },
    },
    watch: {
      finding: {
        immediate: true,
        handler(finding) {
          if (this.draftId !== finding.id) {
            this.resetDraft();
            this.imageFailed = false;
            return;
          }
          const editedFields = EDITABLE_FIELDS.filter(
            (field) => !sameField(field, this.draft[field], this.draftBaseline[field]),
          );
          const preservedEdits = Object.fromEntries(
            editedFields.map((field) => [field, this.draft[field]]),
          );
          const pickedInspection = idOf(this.draftInspection) !== this.draftBaselineInspectionId ? this.draftInspection : null;
          this.resetDraft();
          this.draft = { ...this.draft, ...preservedEdits };
          if (pickedInspection) this.draftInspection = pickedInspection;
          this.refreshedWhileEditing = editedFields.some(
            (field) => !sameField(field, this.draft[field], finding[field]),
          ) || (!!pickedInspection && idOf(pickedInspection) !== idOf(finding.inspection));
        },
      },
    },
    methods: {
      resetDraft() {
        // Draft contains editable primitives only. Nested geometry and linkage never become editable state.
        this.draft = Object.fromEntries(
          EDITABLE_FIELDS.map((field) => [field, this.finding[field]]),
        );
        if (this.draft.severity != null) this.draft.severity = String(this.draft.severity);
        this.draftBaseline = { ...this.draft };
        this.draftInspection = this.finding.inspection || null;
        this.draftBaselineInspectionId = idOf(this.draftInspection);
        this.draftId = this.finding.id;
        this.refreshedWhileEditing = false;
      },
      /** Corrosion / pitting category chip in the marker colours (Cat A green .. Cat D red), any spelling. */
      pittingChip(item) {
        const style = categoryStyle(item && item.name);
        return style.key ? { color: style.color, 'text-color': style.ink } : NEUTRAL_CHIP;
      },
      pittingHint(item) {
        return (item && item.description) || categoryStyle(item && item.name).description || '';
      },
      /** Defect categorization chip in its configured colour. */
      defectChip(item) {
        return item && item.color ? { color: item.color, 'text-color': inkOn(item.color) } : NEUTRAL_CHIP;
      },
      optionsFor(options, current) {
        return current && !options.some((option) => option.name === current)
          ? [...options, { name: current }]
          : options;
      },
      /** The link cannot be removed, only moved: clearing the picker puts the current inspection back. */
      setDraftInspection(inspection) {
        this.draftInspection = inspection || this.finding.inspection || null;
      },
      save() {
        if (this.busy || !this.dirty) return;
        // Keep the full update contract, taking unedited metadata from the latest authoritative row.
        const result = { ...this.finding };
        EDITABLE_FIELDS.forEach((field) => {
          result[field] = this.draft[field];
        });
        // Second argument tells an inspection-mode host what changed: the fields (PUT) and/or the link.
        this.$emit('save', result, {
          fieldsChanged: this.fieldsDirty,
          inspection: this.inspectionDirty ? this.draftInspection : null,
        });
      },
      link() {
        this.$emit(this.linkMode === 'inspection' ? 'link-inspection' : 'link', this.finding);
      },
      unlink() {
        this.$emit(this.linkMode === 'inspection' ? 'unlink-inspection' : 'unlink', this.finding);
      },
      close() {
        if (!this.busy) this.$emit('close');
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '../aiAnalysis/aiAnalysisTokens';
  .verified-anomaly-detail {
    @include ai-analysis-tokens;
    width: 380px;
    flex: 0 0 380px;
    min-width: 0;
    min-height: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    color: var(--aa-text-primary);
    background: var(--aa-surface);
    border-left: 1px solid var(--aa-border-default);
    .verified-detail-header {
      background-color: var(--aa-surface-subtle);
      display: flex;
      flex: 0 0 auto;
      align-items: flex-start;
      justify-content: space-between;
      gap: var(--ds-space-sm);
      padding: var(--ds-space-md);
      border-bottom: 1px solid var(--aa-border-subtle);
    }
    .verified-detail-header > div {
      min-width: 0;
    }
    .eyebrow {
      color: var(--aa-brand);
      font-size: var(--ds-font-caption);
      font-weight: 600;
    }
    h2 {
      font-size: var(--ds-font-body);
      overflow-wrap: anywhere;
      margin: 4px 0;
    }
    p {
      margin: 4px 0 8px;
      font-size: var(--ds-font-body-sm);
      overflow-wrap: anywhere;
    }
    .verified-detail-body {
      flex: 1 1 auto;
      overflow: auto;
      min-height: 0;
    }
    .detail-section {
      padding: var(--ds-space-md);
      border-bottom: 1px solid var(--aa-border-subtle);
    }
    h3 {
      font-size: var(--ds-font-body-sm);
      margin-bottom: var(--ds-space-md);
      color: var(--aa-section-title);
    }
    .linked-component {
      display: inline-flex;
      align-items: center;
      gap: var(--ds-space-xs);
      min-width: 0;
      padding: 4px 6px;
      border: 1px solid var(--aa-border-default);
      border-radius: var(--ds-radius-sm);
      color: var(--aa-text-primary);
      font-weight: 600;
      text-align: left;
    }
    .linked-component span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .linked-component:focus-visible {
      outline: 2px solid var(--aa-focus);
      outline-offset: 2px;
    }
    .linked-component-actions {
      display: flex;
      align-items: center;
      gap: var(--ds-space-xs);
      min-width: 0;
    }
    .muted {
      color: var(--aa-text-muted);
      font-size: var(--ds-font-caption);
    }
    .category-chip {
      flex: none;
      font-weight: 600;
    }
    .draft-notice {
      color: var(--aa-status-stale);
    }
    .v-input {
      margin-bottom: var(--ds-space-md);
    }
    .evidence-image {
      display: block;
      width: 100%;
      max-height: 200px;
      object-fit: contain;
      background: var(--aa-surface-subtle);
      border-radius: var(--ds-radius-sm);
      margin-bottom: var(--ds-space-md);
    }
    dl {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: var(--ds-space-sm);
      font-size: var(--ds-font-caption);
      margin-bottom: var(--ds-space-md);
    }
    dt {
      color: var(--aa-text-muted);
    }
    dd {
      overflow-wrap: anywhere;
    }
    .inspection-field {
      margin-bottom: var(--ds-space-xs);
    }
    .ai-content-notice {
      flex: 0 0 auto;
      display: flex;
      align-items: flex-start;
      gap: var(--ds-space-xs);
      margin: 0;
      padding: var(--ds-space-sm) var(--ds-space-md);
      border-top: 1px solid var(--aa-border-default);
      background: var(--aa-surface-subtle);
      color: var(--aa-text-muted);
      font-size: var(--ds-font-caption);
      line-height: 1.4;
      .v-icon {
        flex: none;
        color: inherit;
      }
    }
    .detail-actions,
    .verified-detail-footer {
      display: flex;
      flex-wrap: wrap;
      gap: var(--ds-space-sm);
    }
    .verified-detail-footer {
      flex: 0 0 auto;
      justify-content: space-between;
      padding: var(--ds-space-md);
      border-top: 1px solid var(--aa-border-default);
    }
    @media (max-width: 1000px) {
      width: 100%;
      flex-basis: auto;
      border-left: none;
      border-top: 1px solid var(--aa-border-default);
    }
  }
  .verified-anomaly-detail.verified-anomaly-detail--embedded {
    width: 100%;
    flex: 1 1 auto;
    height: auto;
    max-height: min(85vh, 820px);
    border: none;
    border-radius: var(--ds-radius-md, 8px);
  }
  .theme--dark .verified-anomaly-detail {
    @include ai-analysis-tokens-dark;
  }
</style>
<style lang="scss">
  // Category dropdown rows render outside the panel (detached menu).
  .category-hint {
    margin-left: 8px;
    font-size: 11px;
    opacity: 0.6;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
